<template>
    <settings-wrapper title="User settings" section="Profile">
        <v-row>
            <v-col>
                <v-card>
                    <v-card-title>Personal information</v-card-title>
                    <v-card-text>
                        <v-row>
                            <v-col>
                                <label>First name</label>
                                <v-sheet color="grey lighten-4 pa-3" rounded class="d-flex align-center">{{ userInfo.given_name }}</v-sheet>
                            </v-col>
                            <v-col>
                                <label>Last name</label>
                                <v-sheet color="grey lighten-4 pa-3" rounded class="d-flex align-center">{{ userInfo.family_name }}</v-sheet>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col>
                                <label>Contact email</label>
                                <v-sheet color="grey lighten-4 pa-3" rounded class="d-flex align-center">{{ userInfo.email }}</v-sheet>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col>
                                <label>Username</label>
                                <v-sheet color="grey lighten-4 pa-3" rounded class="d-flex align-center">{{ userInfo.username }}</v-sheet>
                            </v-col>
                            <v-col>
                                <label>User status</label>
                                <v-sheet color="grey lighten-4 px-3" rounded class="d-flex align-center" height="46">
                                    <v-chip small :color="userInfo.is_active ? 'success' : 'error'">
                                        {{ userInfo.is_active ? 'Active' : 'Inactive' }}
                                    </v-chip>
                                </v-sheet>
                            </v-col>
                        </v-row>
                    </v-card-text>
                    <v-card-actions>
                        <user-personal-info-edit-dialog :user-info="userInfo" />
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>

        <v-row>
            <v-col>
                <v-card>
                    <v-card-title>Identity provider information</v-card-title>
                    <v-card-text>
                        <v-row>
                            <v-col>
                                <label>Account email</label>
                                <v-sheet color="grey lighten-4 pa-3" rounded class="d-flex align-center">{{ userIdPInfo.idp_email }}</v-sheet>
                            </v-col>
                            <v-col>
                                <label>Identity provider</label>
                                <v-sheet color="grey lighten-4 px-3" height="46" rounded class="d-flex align-center">
                                    <v-chip small color="grey lighten-2">{{ userIdPInfo.idp }}</v-chip>
                                </v-sheet>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-row>
            <v-col>
                <v-card>
                    <v-card-title>Privacy settings</v-card-title>
                    <v-card-text>
                        <v-row>
                            <v-col>
                                <label>Newsletter</label>
                                <v-sheet color="grey lighten-4 pa-3" rounded class="d-flex align-center">
                                    <v-checkbox
                                        v-model="newsletter"
                                        :disabled="fetchingUserInfo"
                                        label="Signed up for newsletter"
                                        hide-details
                                        class="ma-0 pa-0"></v-checkbox>
                                </v-sheet>
                            </v-col>
                            <v-col>
                                <label>Analytics</label>
                                <v-sheet color="grey lighten-4 pa-3" rounded class="d-flex align-center">
                                    <v-checkbox
                                        v-model="analyticsOptIn"
                                        :disabled="fetchingUserInfo"
                                        label="Allow use of analytics data"
                                        @click="confirm"
                                        hide-details
                                        class="ma-0 pa-0"></v-checkbox>
                                </v-sheet>
                                <div v-if="!isProduction()">This is non production so analytics is always off.</div>
                            </v-col>
                        </v-row>
                    </v-card-text>
                    <v-card-actions>
                        <div>
                            <v-btn color="primary" text @click="save">Save</v-btn>
                        </div>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>

        <v-row>
            <v-col>
                <h3 class="error--text">Danger Zone</h3>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="6">
                <v-card>
                    <v-card-title>Delete account</v-card-title>
                    <v-card-actions>
                        <div>
                            <EntityDeleteDialog entity-type="account" />
                        </div>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>

        <v-row v-if="isSysadmin" align="center" class="mt-6">
            <v-col cols="3">
                <v-subheader>System Administrator</v-subheader>
            </v-col>
            <v-col cols="9">
                <v-btn color="primary" text :to="{ name: 'admin-overview' }" depressed>manage</v-btn>
            </v-col>
        </v-row>
    </settings-wrapper>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import eventBus from '@/components/EventBus'
import { analyticsMixin } from '@/mixins/analytics'
import { isProduction } from '@/helpers'
import SettingsWrapper from '@/components/SettingsWrapper.vue'
import { createBackendErrorHandler } from '@/utils/errorMessages'
import * as filesStorageErrors from '@/modules/files/errors'
import * as permissionsErrors from '@/utils/errors/permissions'
import * as userErrors from '@/modules/user/errors/settings'

const { getBackendErrorMessage } = createBackendErrorHandler(filesStorageErrors, permissionsErrors, userErrors)

const EntityDeleteDialog = () => import('@/components/EntityDeleteDialog.vue')
const UserPersonalInfoEditDialog = () => import('@/modules/user/components/UserPersonalInfoEditDialog.vue')

export default {
    name: 'UserSettingsProfile',
    mixins: [analyticsMixin],
    components: { SettingsWrapper, EntityDeleteDialog, UserPersonalInfoEditDialog },
    data() {
        return {
            newsletter: true,
            analyticsOptIn: false,
            userIdPInfo: null
        }
    },
    methods: {
        async save() {
            const analyticsChanged = this.userMetadata.analytics_opt_in !== this.analyticsOptIn
            try {
                await this.$store.commit('setUserMetadata', { unsubscribed_from_newsletter: !this.newsletter, analytics_opt_in: this.analyticsOptIn })
                this.$store.dispatch('showSnackBar', {
                    snackBarText: analyticsChanged ? `The page will be reloaded so your changes can take effect` : `User settings saved.`,
                    snackBarTimeout: analyticsChanged ? 4000 : 2000,
                    snackBarIcon: 'check_circle'
                })
                if (analyticsChanged) {
                    window.setTimeout(() => window.location.reload(), 4000)
                }
            } catch (error) {
                const snackBarText = getBackendErrorMessage(error, `Could not save user settings!`)
                if (snackBarText) {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText,
                        snackBarTimeout: 2000,
                        snackBarIcon: 'error'
                    })
                }
            }
        },
        confirm() {
            if (this.analyticsOptIn === false) {
                this.$store.dispatch('showGlobalDialog', {
                    dialogText:
                        'Site analytics data helps us to identify potential and materialized issues faster. If you opt out from analytics, it may take longer to resolve your future support requests.',
                    dialogAction: ['cancel', 'accept'],
                    dialogTitle: 'Confirm opting out'
                })
            }
        },
        isProduction() {
            return isProduction()
        },
        async getUserIdP() {
            try {
                const { data } = await this.$axios.get('/users/idp_info')
                this.userIdPInfo = data
            } catch (error) {}
        }
    },
    computed: {
        ...mapState(['userInfo', 'fetchingUserInfo', 'userMetadata']),
        ...mapGetters(['isSysadmin'])
    },
    watch: {
        userMetadata(data) {
            this.newsletter = !this.userMetadata?.unsubscribed_from_newsletter
            this.analyticsOptIn = this.isAnalyticsEnabled
        }
    },
    created() {
        this.getUserIdP()
        this.analyticsOptIn = this.isAnalyticsEnabled
        this.newsletter = !this.userMetadata?.unsubscribed_from_newsletter
        eventBus.$on('global-dialog-decline', () => {
            this.analyticsOptIn = true
        })
    }
}
</script>
